/**
 * Auth Service
 * 인증 관련 API 호출을 담당하는 서비스
 * 21개 Auth API 엔드포인트 메서드
 */

import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/types/api';
import type {
  LoginRequest,
  UserInfoResponse,
  SessionResponse,
  MeUserInfoResponse,
  FindIdRequest,
  FindIdResponse,
  FindPasswordRequest,
  ChangePasswordRequest,
  VerifyPasswordRequest,
  PostponePasswordChangeRequest,
  PrivacyConsentRequest,
  TermsConsentRequest,
  TermsConsentStatusResponse,
  RejectConsentByCodeRequest,
  RejectConsentByEmailRequest,
  PersonalizationResponse,
  PersonalizationRequest,
  ProfileUpdateRequest,
  ProfileUpdateResponse,
  EmailSubscriptionRequest,
  ActivateRequest,
  ActivateResponse,
} from '@/types/auth';

class AuthService {
  /**
   * 로그인
   * POST /auth/login
   */
  async login(request: LoginRequest): Promise<ApiResponse<UserInfoResponse>> {
    return apiClient.post<UserInfoResponse>('/auth/login', request);
  }

  /**
   * 로그아웃
   * POST /auth/logout
   */
  async logout(): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/logout');
  }

  /**
   * 세션 확인
   * GET /auth/session
   */
  async getSession(): Promise<ApiResponse<SessionResponse>> {
    return apiClient.get<SessionResponse>('/auth/session');
  }

  /**
   * 내 정보 조회
   * GET /auth/me
   */
  async getMe(): Promise<ApiResponse<MeUserInfoResponse>> {
    return apiClient.get<MeUserInfoResponse>('/auth/me');
  }

  /**
   * 아이디 찾기
   * POST /auth/find-id
   */
  async findId(request: FindIdRequest): Promise<ApiResponse<FindIdResponse>> {
    return apiClient.post<FindIdResponse>('/auth/find-id', request);
  }

  /**
   * 비밀번호 찾기
   * POST /auth/find-password
   */
  async findPassword(request: FindPasswordRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/find-password', request);
  }

  /**
   * 비밀번호 변경
   * POST /auth/change-password
   */
  async changePassword(request: ChangePasswordRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/change-password', request);
  }

  /**
   * 비밀번호 확인
   * POST /auth/verify-password
   */
  async verifyPassword(request: VerifyPasswordRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/verify-password', request);
  }

  /**
   * 비밀번호 변경 연기
   * POST /auth/postpone-password-change
   */
  async postponePasswordChange(
    request?: PostponePasswordChangeRequest
  ): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/postpone-password-change', request || {});
  }

  /**
   * 이메일 중복 확인
   * GET /auth/check-email
   */
  async checkEmail(email: string): Promise<ApiResponse<boolean>> {
    return apiClient.get<boolean>(`/auth/check-email?email=${encodeURIComponent(email)}`);
  }

  /**
   * 개인정보 동의
   * POST /auth/privacy-consent
   */
  async privacyConsent(request: PrivacyConsentRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/privacy-consent', request);
  }

  /**
   * 약관 동의
   * POST /auth/terms-consent
   */
  async termsConsent(request: TermsConsentRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/terms-consent', request);
  }

  /**
   * 약관 동의 상태 조회
   * GET /auth/terms-consent-status
   */
  async getTermsConsentStatus(): Promise<ApiResponse<TermsConsentStatusResponse>> {
    return apiClient.get<TermsConsentStatusResponse>('/auth/terms-consent-status');
  }

  /**
   * 동의 거부
   * POST /auth/reject-consent
   */
  async rejectConsent(): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/reject-consent');
  }

  /**
   * 코드로 동의 거부
   * POST /auth/reject-consent-by-code
   */
  async rejectConsentByCode(userCode: string): Promise<ApiResponse<boolean>> {
    const request: RejectConsentByCodeRequest = { userCode };
    return apiClient.post<boolean>('/auth/reject-consent-by-code', request);
  }

  /**
   * 이메일로 동의 거부
   * POST /auth/reject-consent-by-email
   */
  async rejectConsentByEmail(email: string): Promise<ApiResponse<boolean>> {
    const request: RejectConsentByEmailRequest = { email };
    return apiClient.post<boolean>('/auth/reject-consent-by-email', request);
  }

  /**
   * 개인화 설정 조회
   * GET /auth/personalization
   */
  async getPersonalization(): Promise<ApiResponse<PersonalizationResponse>> {
    return apiClient.get<PersonalizationResponse>('/auth/personalization');
  }

  /**
   * 개인화 설정 저장
   * POST /auth/personalization
   */
  async savePersonalization(request: PersonalizationRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/personalization', request);
  }

  /**
   * 프로필 업데이트
   * POST /auth/profile
   */
  async updateProfile(request: ProfileUpdateRequest): Promise<ApiResponse<ProfileUpdateResponse>> {
    return apiClient.post<ProfileUpdateResponse>('/auth/profile', request);
  }

  /**
   * 이메일 구독 설정
   * POST /auth/email-subscription
   */
  async updateEmailSubscription(request: EmailSubscriptionRequest): Promise<ApiResponse<boolean>> {
    return apiClient.post<boolean>('/auth/email-subscription', request);
  }

  /**
   * 매직 링크 활성화
   * POST /auth/activate
   */
  async activate(token: string): Promise<ApiResponse<ActivateResponse>> {
    const request: ActivateRequest = { token };
    return apiClient.post<ActivateResponse>('/auth/activate', request);
  }
}

// 싱글톤 인스턴스 export
export const authService = new AuthService();

